import React, { useEffect } from 'react';
import { VStack, HStack, Box, Button, Text } from '@chakra-ui/react';
import { GiftCarousel } from '../GiftCarousel';
import { DonationContinueSection } from '../DonationContinueSection';
import { useFormContext } from '../context/FormContext';
import { setGift, setNoGift, nextStep, prevStep } from '../context/formActions';
import { canContinueFromStep } from '../validation';
import { donateEvent } from '@kuow/core';
import { CheckboxCircle } from '../../primitives/CheckboxCircle';
import { CheckboxCard } from '../../primitives/CheckboxCard';
import { useDonationFormUi } from '../context/UiContext';

export const GiftStep: React.FC = () => {
  const { state, dispatch } = useFormContext();
  const { showStepNavigator } = useDonationFormUi();
  const { formData, formDetails, validation } = state;

  useEffect(() => {
    donateEvent('Step Gift', state.placement);
  }, [state.placement]);

  if (!formDetails || formDetails.gifts.length === 0) {
    return null;
  }

  const handleGiftSelect = (giftId: number) => {
    const gift = formDetails.gifts.find((g) => g.id === giftId);
    if (!gift) return;

    const priceField = formData.frequency === 'monthly' ? 'price_per_monthly' : 'price_per_single';
    const minPrice = parseFloat(gift[priceField]);
    const currentDonationAmount = formData.customAmount ?? formData.amount ?? 0;

    // The reducer applies the `max(base, current, giftPrice)` bump inside SET_GIFT;
    // this event just records whether the selection required a bump.
    if (currentDonationAmount < minPrice) {
      donateEvent('Unlocked Gift', state.placement);
    }

    donateEvent('Select Gift', state.placement);

    const hasSingleVariant = gift.gift_variants.length === 1;
    const singleVariant = hasSingleVariant ? gift.gift_variants[0] : undefined;

    dispatch(
      setGift({
        giftId,
        ...(singleVariant ? { variantId: singleVariant.id } : {}),
      }),
    );
    dispatch(setNoGift(false));
  };

  const handleVariantSelect = (giftId: number, variantId: number) => {
    dispatch(
      setGift({
        giftId,
        variantId,
      }),
    );
    dispatch(setNoGift(false));
  };

  const handleNoGift = () => {
    if (!formData.noGift) {
      donateEvent('Select No Gift', state.placement);
      dispatch(setNoGift(true));
    }
  };

  const handleContinue = () => {
    if (canContinueFromStep('gift', validation)) {
      dispatch(nextStep());
    }
  };

  const showBackButton = !showStepNavigator;

  const currentDonationAmount = formData.customAmount ?? formData.amount ?? 0;
  const totalAmount = formData.totalDonationAmount ?? formData.amount ?? formData.customAmount ?? 0;
  const selectedGiftId = formData.gift?.giftId ?? null;
  const selectedVariantId = formData.gift?.variantId;

  return (
    <VStack gap={6} align="stretch" textAlign="center" overflow="visible">
      <Box>
        <Text fontSize="md" fontWeight="semibold">
          Choose a Gift
        </Text>
      </Box>

      <Box overflow="visible">
        <GiftCarousel
          gifts={formDetails.gifts}
          frequency={formData.frequency}
          currentDonationAmount={currentDonationAmount}
          selectedGiftId={selectedGiftId}
          selectedVariantId={selectedVariantId}
          onGiftSelect={handleGiftSelect}
          onVariantSelect={handleVariantSelect}
        />
      </Box>

      <Box>
        <CheckboxCard isSelected={formData.noGift} onClick={handleNoGift}>
          <HStack gap={3} align="center">
            <CheckboxCircle isSelected={formData.noGift} />
            <Box textAlign="left">
                  <Text fontSize="sm" fontWeight="semibold">
                No gift
                  </Text>
              <Text fontSize="xs" color="fg.muted">
                Use 100% of my donation to support KUOW
                            </Text>
                          </Box>
              </HStack>
        </CheckboxCard>
            </Box>

      <DonationContinueSection
        amount={totalAmount}
        frequency={formData.frequency}
        buttonLabel="Continue"
        onClick={handleContinue}
        disabled={!canContinueFromStep('gift', validation)}
      />

      {showBackButton ? (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => dispatch(prevStep())}
          alignSelf="center"
        >
          Back
        </Button>
      ) : null}
    </VStack>
  );
};

